/**
 * Keep the payer's details out of browser events (sip-9mr).
 *
 * PayPal's auto-return appends the whole transaction to the confirmation URL as GET parameters:
 *
 *   /services/aufladungsbestaetigung/?PayerID=..&payer_email=..&first_name=..&last_name=..
 *   &address_street=..&address_city=..&address_zip=..&mc_gross=..&cm=||1234|15.00
 *
 * so a real payer's name, email address, street, city, postcode, the amount and the inmate
 * account reference are all in the page URL. Measured 2026-08-07 over 90 days: 11 of 50 issues
 * checked carried it in their latest event, totalling more than 5,500 events, the largest of them
 * frontend errors on that very page.
 *
 * **Why this cannot be done by the backend hook.** `backend/utils.py` has the same scrub, but
 * browser events never reach it: `/public/sentry/` is a tunnel (`sentry_proxy`), and it rewrites
 * only the DSN in the envelope header before forwarding the bytes on. Anything the browser SDK
 * puts in an event travels exactly as sent.
 *
 * Measured against a real tunneled envelope from @sentry/sveltekit 8.55.2, which is where the two
 * fields below come from -- `request.url` is set by the HttpContext integration from
 * `location.href`, and `request.headers.Referer` from `document.referrer`. `transaction` is
 * already the path alone and needs nothing.
 *
 * This is the second line of defence. The first is the confirmation page dropping the parameters
 * from its own URL with `history.replaceState`, which also keeps them out of the browser's history
 * and out of the Referer of every same-origin request the page makes -- which is how they reach
 * *backend* events. Both exist because the replace runs on mount, and an error thrown before that
 * would still carry the URL.
 */

/**
 * Keep a URL's origin and path; redact its query string wholesale.
 *
 * Wholesale rather than by parameter name, matching `_redact_url_query` in backend/utils.py: a
 * denylist of PayPal's field names goes stale the day PayPal adds one, and that reasoning has
 * already lost twice a layer down (sip-4h4, sip-4lm). The path is kept because which page failed
 * is the diagnosis.
 *
 * The `<redacted:N>` shape is the backend's `mask_value`, deliberately: both halves land in the
 * same GlitchTip and should read the same way.
 *
 * @param {unknown} url
 */
export function scrubUrlQuery(url) {
	if (typeof url !== 'string' || !url) return url;

	const mark = url.indexOf('?');
	if (mark === -1 || mark === url.length - 1) return url;

	return `${url.slice(0, mark + 1)}<redacted:${url.length - mark - 1}>`;
}

/**
 * Sentry `beforeSend`: redact the query string out of the URLs an event carries.
 *
 * Returns the event so it can be chained; it must never return undefined, which Sentry reads as
 * "drop this event".
 *
 * @template T
 * @param {T} event
 * @returns {T}
 */
export function scrubEventUrls(event) {
	scrubBreadcrumbUrls(event);

	const request = event && typeof event === 'object' ? /** @type {any} */ (event).request : null;
	if (!request || typeof request !== 'object') return event;

	if ('url' in request) request.url = scrubUrlQuery(request.url);

	const headers = request.headers;
	if (headers && typeof headers === 'object') {
		for (const key of Object.keys(headers)) {
			// The browser SDK writes `Referer`; a python event carries `referer`.
			if (key.toLowerCase() === 'referer') headers[key] = scrubUrlQuery(headers[key]);
		}
	}

	return event;
}

/**
 * The URLs a breadcrumb carries: where a navigation came from and went to, and what an
 * xhr/fetch crumb requested.
 *
 * This one was NOT predicted -- the end-to-end test found it after the other two were already
 * clean, and it is the sharpest reason that test exists. Dropping the query with
 * `history.replaceState` is itself a navigation as far as the SDK is concerned, so the fix at
 * the source *creates* a breadcrumb whose `from` is the URL it just removed. A visitor
 * navigating away from the confirmation page produces the same crumb without any help from us.
 *
 * @param {unknown} event
 */
function scrubBreadcrumbUrls(event) {
	const breadcrumbs = event && typeof event === 'object' ? /** @type {any} */ (event).breadcrumbs : null;
	if (!Array.isArray(breadcrumbs)) return;

	for (const crumb of breadcrumbs) {
		const data = crumb && typeof crumb === 'object' ? crumb.data : null;
		if (!data || typeof data !== 'object') continue;

		for (const field of ['from', 'to', 'url']) {
			if (field in data) data[field] = scrubUrlQuery(data[field]);
		}
	}
}
